"use client";

import { useEffect, useRef } from "react";

// Tunables from the design handoff (ascii-reveal.md).
const GLYPHS = "@#W$9876543210?!abc;:+=-,._  "; // densest to emptiest
const COLUMNS = 64;
const MIN_CELL_CSS = 7; // px
const CONTRAST = 1.2;
const BRIGHTNESS = 0.2;
// Scroll-driven: progress starts when the leading edge enters the viewport and completes
// once the image centre reaches END_AT of the viewport along the travel axis.
const END_AT = 0.45;
const BLEND_START = 0.45;
const FLICKER_RATE = 16; // glyph changes per second at the scramble peak
const FLICKER_SPREAD = 7; // max glyph steps away from the brightness-correct glyph
const REST_FLICKER = true;
const REST_FLICKER_RATE = 2;
const REST_FLICKER_AMOUNT = 0.2;

const MAX_DPR = 2;
const GLYPH_FONT = 'Menlo, Monaco, "Courier New", monospace';

const glsl = (n: number) => n.toFixed(4);

const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = (a_position + 1.0) * 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision highp float;
varying vec2 v_uv;
uniform sampler2D u_image;
uniform sampler2D u_glyph;
uniform vec2 u_resolution;
uniform float u_cell;
uniform float u_blend;
uniform float u_glyph_count;
uniform float u_phase;
uniform float u_flicker;

float hash(vec2 p) {
  p = fract(p * vec2(0.1031, 0.1030));
  p += dot(p, p.yx + 33.33);
  return fract((p.x + p.y) * p.x);
}

vec3 sampleImage(vec2 frag) {
  vec2 uv = clamp(frag / u_resolution, 0.0, 1.0);
  return texture2D(u_image, vec2(uv.x, 1.0 - uv.y)).rgb;
}

void main() {
  vec2 frag = v_uv * u_resolution;
  // Grid anchored at the centre so the portrait stays symmetric in the frame.
  vec2 origin = floor(u_resolution * 0.5);
  vec2 rel = frag - origin;
  vec2 cellCoord = floor(rel / u_cell);
  vec2 center = origin + (cellCoord + 0.5) * u_cell;
  float q = u_cell * 0.25;

  vec3 color = (sampleImage(center) +
                sampleImage(center + vec2(q, q)) + sampleImage(center + vec2(-q, q)) +
                sampleImage(center + vec2(q, -q)) + sampleImage(center + vec2(-q, -q))) * 0.2;

  float luma = dot(color, vec3(0.299, 0.587, 0.114));
  luma = clamp((luma - 0.5) * ${glsl(CONTRAST)} + 0.5 + ${glsl(BRIGHTNESS)}, 0.0, 1.0);
  float glyphIndex = floor((1.0 - luma) * (u_glyph_count - 1.0) + 0.5);

  // Per-cell rate and phase so cells tick independently, like a scrambling counter.
  float speed = 0.6 + 0.8 * hash(cellCoord + 17.0);
  float tick = floor(u_phase * speed + hash(cellCoord) * 8.0);
  float jitter = (hash(cellCoord + tick * vec2(1.618, 2.414)) * 2.0 - 1.0) * u_flicker * ${glsl(FLICKER_SPREAD)};
  glyphIndex = clamp(floor(glyphIndex + jitter + 0.5), 0.0, u_glyph_count - 1.0);

  vec2 local = (rel - cellCoord * u_cell) / u_cell;
  float mask = texture2D(u_glyph, vec2((glyphIndex + local.x) / u_glyph_count, 1.0 - local.y)).r;

  vec3 ink = clamp(color * 1.35 + 0.08, 0.0, 1.0);
  vec3 ascii = mix(color * 0.22, ink, mask);

  vec3 photo = sampleImage(frag);
  gl_FragColor = vec4(mix(ascii, photo, u_blend), 1.0);
}`;

/** Where the image box sits inside the canvas, in percent of the frame (drawn object-cover). */
export interface Crop {
  w: number;
  h: number;
  left: number;
  top: number;
}

interface AsciiRevealProps {
  src: string;
  crop: Crop;
  /** Axis the image travels along on screen: "x" for the horizontal track, "y" for vertical scroll. */
  axis: "x" | "y";
  /**
   * Called with true when scroll progress reaches the end and false when scrolling back out of it.
   * Called with true for good if WebGL/decoding fails.
   */
  onRevealedChange: (revealed: boolean) => void;
}

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Could not create shader");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "");
  return shader;
}

function uploadTexture(
  gl: WebGLRenderingContext,
  unit: number,
  texture: WebGLTexture | null,
  source: TexImageSource,
) {
  const tex = texture ?? gl.createTexture();
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  return tex;
}

// Draws the image object-cover inside the crop box, at the canvas's device-pixel size.
function drawCover(image: HTMLImageElement, width: number, height: number, crop: Crop) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const boxX = (crop.left / 100) * width;
  const boxY = (crop.top / 100) * height;
  const boxW = (crop.w / 100) * width;
  const boxH = (crop.h / 100) * height;
  const scale = Math.max(boxW / image.naturalWidth, boxH / image.naturalHeight);
  const drawW = image.naturalWidth * scale;
  const drawH = image.naturalHeight * scale;

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, boxX + (boxW - drawW) / 2, boxY + (boxH - drawH) / 2, drawW, drawH);
  return canvas;
}

// One row of GLYPHS, one cell per glyph, white on black.
function buildGlyphAtlas(cell: number) {
  const canvas = document.createElement("canvas");
  canvas.width = GLYPHS.length * cell;
  canvas.height = cell;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(cell * 0.92)}px ${GLYPH_FONT}`;
  for (let i = 0; i < GLYPHS.length; i++) {
    ctx.fillText(GLYPHS[i], i * cell + cell / 2, cell * 0.54);
  }
  return canvas;
}

export function AsciiReveal({ src, crop, axis, onRevealedChange }: AsciiRevealProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onRevealedChangeRef = useRef(onRevealedChange);
  onRevealedChangeRef.current = onRevealedChange;
  const { w: cropW, h: cropH, left: cropLeft, top: cropTop } = crop;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const box: Crop = { w: cropW, h: cropH, left: cropLeft, top: cropTop };
    let disposed = false;
    let failed = false;
    let revealed = false;

    let gl: WebGLRenderingContext | null = null;
    let uniforms: {
      resolution: WebGLUniformLocation | null;
      cell: WebGLUniformLocation | null;
      blend: WebGLUniformLocation | null;
      phase: WebGLUniformLocation | null;
      flicker: WebGLUniformLocation | null;
    } | null = null;
    let image: HTMLImageElement | null = null;
    let imageTex: WebGLTexture | null = null;
    let glyphTex: WebGLTexture | null = null;

    let cell = 0;
    let blend = 0;
    let phase = 0;
    let flicker = REST_FLICKER ? REST_FLICKER_AMOUNT : 0;
    let lastTime = 0;
    let lastTick = -1;
    let lastProgress = -1;
    let loopRaf = 0;

    const setRevealed = (next: boolean) => {
      if (disposed || next === revealed) return;
      revealed = next;
      onRevealedChangeRef.current(next);
    };

    // Falls back to the plain photo for good.
    const fail = () => {
      failed = true;
      cancelAnimationFrame(loopRaf);
      loopRaf = 0;
      visibleObserver.disconnect();
      setRevealed(true);
    };

    const draw = () => {
      if (!gl || !uniforms || !imageTex || !glyphTex) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(uniforms.cell, cell);
      gl.uniform1f(uniforms.blend, blend);
      gl.uniform1f(uniforms.phase, phase);
      gl.uniform1f(uniforms.flicker, flicker);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const rebuild = (force = false) => {
      if (!gl || !image) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      const width = Math.round(rect.width * dpr);
      const height = Math.round(rect.height * dpr);
      if (!force && imageTex && canvas.width === width && canvas.height === height) return;

      canvas.width = width;
      canvas.height = height;
      cell = Math.round(Math.max(MIN_CELL_CSS * dpr, width / COLUMNS));
      imageTex = uploadTexture(gl, 0, imageTex, drawCover(image, width, height, box));
      glyphTex = uploadTexture(gl, 1, glyphTex, buildGlyphAtlas(cell));
      draw();
    };

    // The CPU accumulates the phase, so changing the rate slows the counter without jumps.
    const advancePhase = (now: number, rate: number) => {
      if (lastTime) phase += (Math.min(now - lastTime, 100) / 1000) * rate;
      lastTime = now;
    };

    // 0 when the leading edge enters the viewport, 1 when the centre reaches END_AT.
    const scrollProgress = () => {
      const rect = canvas.getBoundingClientRect();
      const [pos, size, viewport] =
        axis === "x"
          ? [rect.left, rect.width, window.innerWidth]
          : [rect.top, rect.height, window.innerHeight];
      const start = viewport + size / 2;
      const end = END_AT * viewport;
      return Math.min(1, Math.max(0, (start - (pos + size / 2)) / (start - end)));
    };

    const restAmount = REST_FLICKER ? REST_FLICKER_AMOUNT : 0;
    const restRate = REST_FLICKER ? REST_FLICKER_RATE : 0;

    // Polled every frame because the desktop track moves by transform and keeps easing after the scroll stops.
    // Glyphs keep ticking over time; how much they scramble and how far the photo has blended is scroll-driven.
    // Keeps running after the end so scrolling back plays the reveal in reverse.
    const loop = (now: number) => {
      if (disposed || failed) return;
      const t = scrollProgress();

      const blendT = Math.max(0, (t - BLEND_START) / (1 - BLEND_START));
      blend = -(Math.cos(Math.PI * blendT) - 1) / 2; // easeInOutSine
      const energy = smoothstep(0, 0.08, t) * (1 - smoothstep(0.3, 0.95, t));
      flicker = (restAmount + (1 - restAmount) * energy) * (1 - smoothstep(0.6, 1, t));
      advancePhase(now, restRate + (FLICKER_RATE - restRate) * energy);

      const done = t >= 1;
      const tick = Math.floor(phase * 4);
      // At the end the real photo covers the canvas, so skip drawing.
      if (imageTex && !done && (t !== lastProgress || tick !== lastTick)) {
        lastProgress = t;
        lastTick = tick;
        draw();
      }
      if (imageTex) setRevealed(done);

      loopRaf = requestAnimationFrame(loop);
    };

    const init = () => {
      gl = canvas.getContext("webgl", { alpha: false, antialias: false });
      if (!gl || gl.isContextLost()) return fail();

      const program = gl.createProgram();
      if (!program) return fail();
      try {
        gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
        gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
      } catch {
        return fail();
      }
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return fail();
      gl.useProgram(program);

      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, "a_position");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      gl.uniform1i(gl.getUniformLocation(program, "u_image"), 0);
      gl.uniform1i(gl.getUniformLocation(program, "u_glyph"), 1);
      gl.uniform1f(gl.getUniformLocation(program, "u_glyph_count"), GLYPHS.length);
      uniforms = {
        resolution: gl.getUniformLocation(program, "u_resolution"),
        cell: gl.getUniformLocation(program, "u_cell"),
        blend: gl.getUniformLocation(program, "u_blend"),
        phase: gl.getUniformLocation(program, "u_phase"),
        flicker: gl.getUniformLocation(program, "u_flicker"),
      };

      const img = new window.Image();
      img.decoding = "async";
      img.src = src;
      img.decode().then(
        () => {
          if (disposed) return;
          image = img;
          rebuild(true);
          resizeObserver.observe(canvas);
          visibleObserver.observe(canvas);
        },
        fail,
      );
    };

    const handleContextLost = () => fail();

    const resizeObserver = new ResizeObserver(() => rebuild());

    // The loop only runs while the canvas is on screen.
    const visibleObserver = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible && !loopRaf) loopRaf = requestAnimationFrame(loop);
      if (!visible && loopRaf) {
        cancelAnimationFrame(loopRaf);
        loopRaf = 0;
      }
    });

    // The section mounts both the desktop and the mobile trees and hides one with CSS, so the
    // WebGL context is only created once the canvas gets within a viewport of the screen.
    const initObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        initObserver.disconnect();
        init();
      },
      { rootMargin: "100%" },
    );

    canvas.addEventListener("webglcontextlost", handleContextLost);
    initObserver.observe(canvas);

    return () => {
      disposed = true;
      cancelAnimationFrame(loopRaf);
      initObserver.disconnect();
      visibleObserver.disconnect();
      resizeObserver.disconnect();
      canvas.removeEventListener("webglcontextlost", handleContextLost);
    };
  }, [src, axis, cropW, cropH, cropLeft, cropTop]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="absolute inset-0 block h-full w-full"
    />
  );
}
